import { Play, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import flowIcon from '@/assets/icons/flow.svg';
import slide1 from '@/assets/marketing/slide-1.png';
import slide2 from '@/assets/marketing/slide-2.png';
import slide3 from '@/assets/marketing/slide-3.png';
import slide4 from '@/assets/marketing/slide-4.png';
import slide5 from '@/assets/marketing/slide-5.png';
import slide6 from '@/assets/marketing/slide-6.png';
import { AppShell } from '@/components/layout/AppShell';
import { cn } from '@/lib/utils';

interface TutorialVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  src: string;
}

// Static tutorial library until the real CMS-backed source lands. Tuple
// typing (readonly [...]) keeps element types as concrete `TutorialVideo`
// rather than `TutorialVideo | undefined` under noUncheckedIndexedAccess.
const TUTORIAL_VIDEOS = [
  {
    id: 'setup',
    title: 'How to Setup Workflow?',
    description: 'Watch short videos to quickly learn how to build and manage your workflows.',
    thumbnail: slide1,
    src: '/video.mp4',
  },
  {
    id: 'multistep',
    title: 'Can I automate multiple steps in one workflow?',
    description: 'Watch short videos to quickly learn how to build and manage your workflows.',
    thumbnail: slide2,
    src: '/video.mp4',
  },
  {
    id: 'integrations',
    title: 'How do I connect apps or integrations?',
    description: 'Watch short videos to quickly learn how to build and manage your workflows.',
    thumbnail: slide3,
    src: '/video.mp4',
  },
  {
    id: 'triggers',
    title: 'What should I check if a trigger is not working?',
    description: 'Watch short videos to quickly learn how to build and manage your workflows.',
    thumbnail: slide4,
    src: '/video.mp4',
  },
  {
    id: 'actions',
    title: 'How do I add actions to a workflow?',
    description: 'Watch short videos to quickly learn how to build and manage your workflows.',
    thumbnail: slide5,
    src: '/video.mp4',
  },
  {
    id: 'face-verification',
    title: 'How to do face verification?',
    description: 'Watch short videos to quickly learn how to build and manage your workflows.',
    thumbnail: slide6,
    src: '/video.mp4',
  },
] as const satisfies readonly TutorialVideo[];

const DEFAULT_VIDEO: TutorialVideo = TUTORIAL_VIDEOS[2];

// First authenticated screen. Three stacked regions:
//   1. Hero CTA — "Create your first workflow" with a primary action.
//   2. Tutorial library — left rail of clickable thumbnails next to the
//      currently-playing video.
//   3. Empty state — shown until the tenant has any workflows.
//
// Workflow CRUD wiring lands in the next PR; New Workflow CTAs route to
// the placeholder `/workflows/new` slot for now.
export function DashboardScreen() {
  const [activeVideoId, setActiveVideoId] = useState<string>(DEFAULT_VIDEO.id);
  const activeVideo: TutorialVideo =
    TUTORIAL_VIDEOS.find((v) => v.id === activeVideoId) ?? DEFAULT_VIDEO;

  return (
    <AppShell title="Dashboard">
      <div className="flex flex-col gap-4 lg:gap-5 text-black-logo">
        <header className="flex items-center justify-between flex-wrap gap-6 gap-y-2">
          <div className="space-y-1.5">
            <h2 className="text-lg lg:text-xl lg:leading-6 font-semibold">
              Everything Starts Here - Create Your First Workflow
            </h2>
            <p className="text-sm lg:text-base leading-[19px]">
              Create a workflow to start verifying - all in one place.
            </p>
          </div>
          <Link to="/workflows/new" className="btn btn-primary btn-small">
            <Plus className="w-3 h-3" />
            <span>New Workflow</span>
          </Link>
        </header>

        <section className="grid xl:grid-cols-7 2xl:grid-cols-6 gap-4 lg:gap-5">
          <aside className="xl:col-span-3 2xl:col-span-2 border border-grayEF rounded-2xl p-4 space-y-4.5">
            <div className="space-y-1">
              <h3 className="text-base lg:text-lg lg:leading-5.5 font-medium">
                Need Help with Workflows?
              </h3>
              <p className="text-sm leading-[17px] text-gray66">
                Watch short videos to quickly learn how to build and manage your workflows.
              </p>
            </div>
            <ul className="flex flex-col gap-4 lg:gap-5">
              {TUTORIAL_VIDEOS.map((video) => {
                const isActive = video.id === activeVideo.id;
                return (
                  <li key={video.id}>
                    <button
                      type="button"
                      onClick={() => setActiveVideoId(video.id)}
                      aria-pressed={isActive}
                      className={cn(
                        'w-full flex items-center gap-3 rounded-xl border border-transparent text-start transition-all duration-300 cursor-pointer',
                        isActive && 'p-2.5 border-primary shadow-elevation-2',
                      )}
                    >
                      <div className="rounded-lg overflow-hidden w-full max-h-[86px] max-w-[86px] sm:max-w-[136px] aspect-video relative shrink-0">
                        <img
                          src={video.thumbnail}
                          alt=""
                          aria-hidden
                          className="w-full h-full object-cover"
                        />
                        <span
                          className="absolute top-1/2 start-1/2 -translate-y-1/2 -translate-x-1/2 w-4.5 h-4.5 text-white"
                          aria-hidden
                        >
                          <Play className="w-full h-full fill-white" />
                        </span>
                      </div>
                      <div className="space-y-1 lg:space-y-2 min-w-0">
                        <h4 className="text-base lg:leading-[19px] font-medium">{video.title}</h4>
                        <p className="text-sm leading-[17px] text-gray66">{video.description}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <div className="xl:col-span-4 2xl:col-span-4">
            <div className="w-full space-y-4 lg:space-y-5">
              <div className="w-full rounded-2xl overflow-hidden text-black/20 border border-grayEF aspect-video">
                {/* `key` re-mounts the element on video change so the browser
                    actually swaps the source instead of holding the last
                    frame. autoplay carries over from the HTML reference. */}
                <video
                  key={activeVideo.id}
                  controls
                  autoPlay
                  muted
                  className="w-full h-full aspect-video"
                >
                  <source src={activeVideo.src} type="video/mp4" />
                </video>
              </div>
              <div className="space-y-1 lg:space-y-2">
                <h3 className="text-lg lg:text-2xl lg:leading-[29px] font-medium">
                  {activeVideo.title}
                </h3>
                <p className="text-base lg:text-xl lg:leading-6 text-gray66">
                  {activeVideo.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="border border-surface-soft-border bg-surface-soft shadow-elevation-soft rounded-2xl flex items-center justify-center p-5 py-8 lg:h-[340px]">
          <div className="flex flex-col items-center gap-4 lg:gap-6 max-w-[315px] w-full text-center">
            <div className="space-y-2.5">
              <div className="w-12 h-12 rounded-full bg-blue-tint text-primary shrink-0 mx-auto flex items-center justify-center">
                <img src={flowIcon} alt="" aria-hidden className="w-7.5 h-7.5" />
              </div>
              <p className="text-base lg:leading-[19px] font-medium text-black/70">
                You don&apos;t have any workflows yet. Create your first workflow to get started.
              </p>
            </div>
            <Link to="/workflows/new" className="btn btn-primary btn-small">
              <Plus className="w-3 h-3" />
              <span>New Workflow</span>
            </Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
